import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Divider, TextField, IconButton, FormGroup, FormControlLabel, Box } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FormBuilder from 'components/shared/form/FormBuilder';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import CheckIcon from '@mui/icons-material/Check';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { SurveyParams } from '../types';
import { getSurvey, putSurvey } from 'services/surveyService';
import { Survey } from 'models/survey';
import { useAppDispatch } from 'hooks';
import { openNotification } from 'services/notificationService/notificationSlice';
import { MetHeader3, PrimaryButton, SecondaryButton } from 'components/shared/common';
import { Breadcrumb } from 'components/public/dashboard/Breadcrumb';
import FormBuilderSkeleton from './FormBuilderSkeleton';
import { FormBuilderData } from 'components/shared/form/FormBuilder/types';
import { getEngagement } from 'services/engagementService';
import { Engagement } from 'models/engagement';
import { openNotificationModal } from 'services/notificationModalService/notificationModalSlice';
import axios from 'axios';
import { AutoSaveSnackBar } from './AutoSaveSnackBar';
import { AdditionalSettings, SurveySwitch } from './AdditionalSettings';
import { BuilderTabs, tabIds } from './BuilderTabs';
import { ReportSettingsPanel, ReportSettingsPanelHandle } from './ReportSettingsPanel';
import { ExportSettingsPanel } from './ExportSettingsPanel';
import { getSurveyEditRules, isEngagementClosed, SURVEY_LOCKED_MESSAGE } from './surveyEditRules';
import { debounce, throttle } from 'lodash';
import { BaseTheme, Palette } from 'styles/Theme';

const TAB_QUESTIONS = 'questions';
const TAB_REPORT = 'report';
const TAB_EXPORT = 'export';

// Intervale before showing again that a locked survey can't be changed.
const LOCKED_NOTICE_INTERVAL = 5000;

// The wizard builder's page tabs. Track to keep clickable on a locked survey.
const PAGE_TAB_SELECTOR = '[ref="gotoPage"]';

// Makes the builder inert apart from those page tabs. Clicks land on the wrapper instead.
const LOCKED_BUILDER_SX = {
    cursor: 'not-allowed',
    '& .formio': { pointerEvents: 'none' },
    [`& .formio .wizard-pages ${PAGE_TAB_SELECTOR}`]: { pointerEvents: 'auto', cursor: 'pointer' },
};

// Formio's builder grid gets its layout from Bootstrap 5 (formio.scss), not MUI's
// theme, so this uses Bootstrap's breakpoint (576px) rather than MUI's xs/sm (600px). Below it,
// formcomponents/formarea stack instead of sitting side by side, so there's nothing to align to.
const FORMIO_BREAKPOINT_SM = '@media (min-width:576px)';
const FORMAREA_LEFT = '220px';

const NAME_FIELD_SX = {
    width: '100%',
    maxWidth: '600px',
    '& .MuiInputBase-input': {
        ...BaseTheme.typography.h3,
        color: Palette.primary.main,
        padding: '2px 8px',
    },
};

const NAME_ICON_BUTTON_SX = {
    '&:hover': { backgroundColor: 'transparent' },
};

interface SurveyForm {
    id: string;
    form_json: unknown;
    name: string;
    is_hidden: boolean;
    is_template: boolean;
}

const SurveyFormBuilder = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { surveyId } = useParams<SurveyParams>();
    const [searchParams] = useSearchParams();

    const [savedSurvey, setSavedSurvey] = useState<Survey | null>(null);
    const [formData, setFormData] = useState<(unknown & { components: unknown[] }) | null>(null);

    const [loading, setLoading] = useState(true);
    const [isNameFocused, setIsNamedFocused] = useState(false);
    const [name, setName] = useState(savedSurvey ? savedSurvey.name : '');
    const [draftName, setDraftName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [savedEngagement, setSavedEngagement] = useState<Engagement | null>(null);

    const [formDefinition, setFormDefinition] = useState<FormBuilderData>({ display: 'form', components: [] });
    const isMultiPage = formDefinition.display === 'wizard';
    const [isHiddenSurvey, setIsHiddenSurvey] = useState(savedSurvey ? savedSurvey.is_hidden : false);
    const [isTemplateSurvey, setIsTemplateSurvey] = useState(savedSurvey ? savedSurvey.is_template : false);

    const editRules = useMemo(() => getSurveyEditRules(savedEngagement), [savedEngagement]);
    const canEdit = editRules.canEdit;

    const [autoSaveNotificationOpen, setAutoSaveNotificationOpen] = useState(false);
    // The export tab can't be opened directly until the report tab has been seen this visit, so a
    // link to it lands on the report tab instead.
    const [tab, setTab] = useState(
        [TAB_REPORT, TAB_EXPORT].includes(searchParams.get('tab') ?? '') ? TAB_REPORT : TAB_QUESTIONS,
    );
    const [reportVisited, setReportVisited] = useState(false);
    // Question the report tab should open on, when reached from the export tab's link.
    const [reportFocusKey, setReportFocusKey] = useState<string>();
    const AUTO_SAVE_INTERVAL = 5000;

    useEffect(() => {
        loadSurvey();
    }, []);

    useEffect(() => {
        if (tab === TAB_REPORT) {
            setReportVisited(true);
        }
    }, [tab]);

    useEffect(() => {
        if (editRules.message) {
            dispatch(openNotification({ severity: editRules.severity, text: editRules.message }));
        }
    }, [savedEngagement]);

    const loadSurvey = async () => {
        if (isNaN(Number(surveyId))) {
            navigate('/surveys');
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'The survey id passed was erroneous',
                }),
            );
            return;
        }
        try {
            const loadedSurvey = await getSurvey(Number(surveyId));
            setSavedSurvey(loadedSurvey);
            const loadedFormJson = loadedSurvey?.form_json || { display: 'form', components: [] };
            setFormDefinition(loadedFormJson);
            setFormData(loadedFormJson);
            setName(loadedSurvey.name);
            setIsHiddenSurvey(loadedSurvey.is_hidden);
            setIsTemplateSurvey(loadedSurvey.is_template);
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred while loading saved survey',
                }),
            );
            navigate('/surveys');
        }
    };

    useEffect(() => {
        if (savedSurvey) {
            loadEngagement();
        }
    }, [savedSurvey]);

    const loadEngagement = async () => {
        if (!savedSurvey?.engagement_id) {
            setLoading(false);
            return;
        }

        try {
            const loadedEngagement = await getEngagement(Number(savedSurvey.engagement_id));
            setSavedEngagement(loadedEngagement);
            setLoading(false);
        } catch (error) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred while loading saved engagement data',
                }),
            );
            navigate('/survey/listing');
        }
    };

    const startEditingName = () => {
        setDraftName(name);
        setIsNamedFocused(true);
    };

    const cancelEditingName = () => {
        setIsNamedFocused(false);
    };

    const commitName = () => {
        setName(draftName.trim() || name);
        setIsNamedFocused(false);
    };

    const currentValuesRef = useRef({ name, isHiddenSurvey, isTemplateSurvey });

    useEffect(() => {
        currentValuesRef.current = { name, isHiddenSurvey, isTemplateSurvey };
    }, [name, isHiddenSurvey, isTemplateSurvey]);

    const autoSaveForm = async (newForm: SurveyForm) => {
        try {
            await putSurvey(newForm);
            setAutoSaveNotificationOpen(true);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const responseData = error.response?.data;
                const errorMessage =
                    typeof responseData === 'string'
                        ? responseData
                        : responseData?.message || 'Error occurred while auto-saving survey';
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: errorMessage,
                    }),
                );
            } else {
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: 'Error occurred while auto-saving survey',
                    }),
                );
            }
        }
    };

    const autoSaveFormRef = useRef(autoSaveForm);
    useEffect(() => {
        autoSaveFormRef.current = autoSaveForm;
    });

    const debounceAutoSaveForm = useRef(
        debounce((form: FormBuilderData) => {
            const { name, isHiddenSurvey, isTemplateSurvey } = currentValuesRef.current;
            autoSaveFormRef.current({
                id: String(surveyId),
                form_json: form,
                name: name,
                is_hidden: isHiddenSurvey,
                is_template: isTemplateSurvey,
            });
        }, AUTO_SAVE_INTERVAL),
    ).current;

    // Says why the survey is locked the moment it's touched.
    const notifyLocked = useRef(
        throttle(
            () => {
                dispatch(openNotification({ severity: 'error', text: SURVEY_LOCKED_MESSAGE }));
            },
            LOCKED_NOTICE_INTERVAL,
            { trailing: false },
        ),
    ).current;

    // Formio reorders pages on mousedown but switches page on click. Blocking only mousedown stops
    // the reorder and leaves paging through the survey working.
    const handleLockedMouseDown = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!(event.target as HTMLElement).closest(PAGE_TAB_SELECTOR)) {
            notifyLocked();
        }
    };

    // Read through a ref so handleFormChange can stay referentially stable, which is what keeps
    // the memoized FormBuilder from re-rendering and leaking formio listeners.
    const canEditRef = useRef(canEdit);
    useEffect(() => {
        canEditRef.current = canEdit;
    }, [canEdit]);

    const handleFormChange = useCallback(
        (form: FormBuilderData) => {
            if (!form?.components) {
                return;
            }
            setFormData(form);
            if (canEditRef.current) {
                debounceAutoSaveForm(form);
            } else {
                notifyLocked();
            }
        },
        [debounceAutoSaveForm, notifyLocked],
    );

    const doSaveForm = async () => {
        await putSurvey({
            id: String(surveyId),
            form_json: formData,
            name: name,
            is_hidden: isHiddenSurvey,
            is_template: isTemplateSurvey,
        });
    };

    const handleSaveForm = async (nextTab: string = TAB_REPORT) => {
        if (!canEdit) {
            setTab(nextTab);
            return;
        }

        if (!savedSurvey) {
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Unable to build survey, please reload',
                }),
            );
            return;
        }

        try {
            setIsSaving(true);
            await doSaveForm();
            dispatch(
                openNotification({
                    severity: 'success',
                    text: savedEngagement?.name
                        ? `Your survey has been saved to "${savedEngagement.name}".`
                        : 'Your survey has been saved.',
                }),
            );

            // formDefinition drives FormBuilder's remount key, so only sync it here (on save) -
            // not on every keystroke, which would remount the builder mid-typing.
            if (formData) {
                setFormDefinition(formData as FormBuilderData);
            }

            setTab(nextTab);
            setIsSaving(false);
        } catch (error) {
            setIsSaving(false);
            if (axios.isAxiosError(error)) {
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: error.response?.data.message,
                    }),
                );
            } else {
                dispatch(
                    openNotification({
                        severity: 'error',
                        text: 'Error occurred while saving survey',
                    }),
                );
            }
        }
    };

    const reportSettingsRef = useRef<ReportSettingsPanelHandle>(null);
    const exportSettingsRef = useRef<ReportSettingsPanelHandle>(null);

    // Switching tabs should never discard unsaved work: leaving the questions tab saves the
    // survey form, leaving a settings tab flushes its pending changes - and a failed save keeps the
    // admin where they are, edits intact. A locked survey has no unsaved work to flush, so every
    // tab stays browsable without a save attempt.
    const handleTabChange = async (nextTab: string, focusQuestionKey?: string) => {
        if (nextTab === tab || (nextTab === TAB_EXPORT && !reportVisited)) {
            return;
        }
        if (!canEdit) {
            setReportFocusKey(focusQuestionKey);
            setTab(nextTab);
            return;
        }
        if (tab === TAB_QUESTIONS) {
            await handleSaveForm(nextTab);
            return;
        }
        const panel = tab === TAB_REPORT ? reportSettingsRef.current : exportSettingsRef.current;
        if (panel && !(await panel.save())) {
            return;
        }
        setReportFocusKey(focusQuestionKey);
        setTab(nextTab);
    };

    if (loading) {
        return <FormBuilderSkeleton />;
    }

    return (
        <Box sx={{ pt: 3, backgroundColor: Palette.background.light }}>
            <Breadcrumb
                items={[
                    { label: 'Surveys', to: '/surveys' },
                    { label: name, to: `/surveys/${surveyId}/submit` },
                    { label: 'Edit survey' },
                ]}
            />
            <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                <Stack direction="row" justifyContent="flex-start" alignItems="center">
                    {!isNameFocused ? (
                        <>
                            <MetHeader3 sx={{ p: 0.5, color: Palette.primary.main }} onClick={startEditingName}>
                                {name}
                            </MetHeader3>
                            <IconButton
                                size="small"
                                onClick={startEditingName}
                                color="inherit"
                                disableRipple
                                aria-label="Edit survey name"
                                sx={NAME_ICON_BUTTON_SX}
                            >
                                <BorderColorIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <TextField
                                autoFocus
                                value={draftName}
                                onChange={(event) => setDraftName(event.target.value)}
                                onBlur={cancelEditingName}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        commitName();
                                    } else if (event.key === 'Escape') {
                                        cancelEditingName();
                                    }
                                }}
                                inputProps={{ 'aria-label': 'Survey name' }}
                                sx={NAME_FIELD_SX}
                            />
                            <IconButton
                                size="small"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={commitName}
                                color="inherit"
                                disableRipple
                                aria-label="Save survey name"
                                sx={NAME_ICON_BUTTON_SX}
                            >
                                <CheckIcon sx={{ fontSize: '1.25rem' }} />
                            </IconButton>
                        </>
                    )}
                </Stack>
                <Divider />
            </Box>
            <BuilderTabs
                tabs={[
                    {
                        value: TAB_QUESTIONS,
                        label: 'Survey questions',
                        icon: <ListAltOutlinedIcon />,
                    },
                    {
                        value: TAB_REPORT,
                        label: 'Public report settings',
                        icon: <AssessmentOutlinedIcon />,
                    },
                    {
                        value: TAB_EXPORT,
                        label: 'Public/Proponent export settings',
                        icon: <FileDownloadOutlinedIcon />,
                        disabled: !reportVisited,
                        disabledTitle: 'Review Public report settings first',
                    },
                ]}
                value={tab}
                onChange={handleTabChange}
            />
            {tab === TAB_QUESTIONS && (
                <>
                    <Box
                        role="tabpanel"
                        id={tabIds(TAB_QUESTIONS).panel}
                        aria-labelledby={tabIds(TAB_QUESTIONS).tab}
                        sx={{ px: { xs: 2, md: 3 } }}
                    >
                        <Stack
                            spacing={1}
                            sx={{ pt: 1, borderBottom: `1px solid ${Palette.border.default}`, pb: 2, mb: 2 }}
                        >
                            <Box
                                sx={{ position: 'relative', ...(canEdit ? {} : LOCKED_BUILDER_SX) }}
                                onMouseDownCapture={canEdit ? undefined : handleLockedMouseDown}
                            >
                                <Box
                                    sx={{
                                        position: 'static',
                                        mb: 2,
                                        zIndex: 2,
                                        [FORMIO_BREAKPOINT_SM]: {
                                            position: 'absolute',
                                            top: 0,
                                            left: FORMAREA_LEFT,
                                            right: 0,
                                            mb: 0,
                                        },
                                    }}
                                >
                                    <FormGroup sx={{ alignItems: 'flex-start' }}>
                                        <FormControlLabel
                                            sx={{ m: 0, gap: '10px' }}
                                            slotProps={{
                                                typography: { sx: { fontSize: '15px', color: Palette.text.primary } },
                                            }}
                                            control={
                                                <SurveySwitch
                                                    checked={isMultiPage}
                                                    onChange={() => {
                                                        if (!canEdit) {
                                                            notifyLocked();
                                                            return;
                                                        }
                                                        dispatch(
                                                            openNotificationModal({
                                                                open: true,
                                                                data: {
                                                                    header: 'Change Survey Type',
                                                                    subText: [
                                                                        {
                                                                            text: `You will be changing the survey type from ${
                                                                                isMultiPage
                                                                                    ? 'multi page to single page'
                                                                                    : 'single page to multi page'
                                                                            }.`,
                                                                        },
                                                                        {
                                                                            text: 'You will lose all current progress if you do.',
                                                                            bold: true,
                                                                        },
                                                                        {
                                                                            text: 'Do you want to change this survey type?',
                                                                        },
                                                                    ],
                                                                    handleConfirm: () => {
                                                                        setFormDefinition({
                                                                            display: isMultiPage ? 'form' : 'wizard',
                                                                            components: [],
                                                                        });
                                                                    },
                                                                },
                                                                type: 'confirm',
                                                            }),
                                                        );
                                                    }}
                                                />
                                            }
                                            label="Multi-page"
                                        />
                                    </FormGroup>
                                </Box>
                                <FormBuilder
                                    handleFormChange={handleFormChange}
                                    savedForm={formDefinition}
                                    isLoading={loading}
                                />
                            </Box>
                            <Box
                                sx={{
                                    pl: 0,
                                    [FORMIO_BREAKPOINT_SM]: { pl: FORMAREA_LEFT },
                                }}
                            >
                                <AdditionalSettings
                                    isTemplateSurvey={isTemplateSurvey}
                                    onTemplateChange={setIsTemplateSurvey}
                                    isHiddenSurvey={isHiddenSurvey}
                                    onHiddenChange={setIsHiddenSurvey}
                                    disabled={Boolean(savedSurvey?.engagement_id)}
                                />
                            </Box>
                        </Stack>
                    </Box>
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            mt: 3,
                            py: 2,
                            px: { xs: 2, md: 3 },
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            backgroundColor: Palette.background.default,
                            borderTop: `1px solid ${Palette.border.default}`,
                            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.06)',
                        }}
                    >
                        {canEdit && (
                            <SecondaryButton
                                disabled={!formData}
                                loading={isSaving}
                                onClick={() => handleSaveForm(TAB_QUESTIONS)}
                            >
                                Save
                            </SecondaryButton>
                        )}
                        <PrimaryButton
                            disabled={!formData}
                            loading={isSaving}
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => handleSaveForm(TAB_REPORT)}
                        >
                            Next: Public report settings
                        </PrimaryButton>
                    </Box>
                </>
            )}
            {tab === TAB_REPORT && (
                <Box role="tabpanel" id={tabIds(TAB_REPORT).panel} aria-labelledby={tabIds(TAB_REPORT).tab}>
                    <ReportSettingsPanel
                        ref={reportSettingsRef}
                        surveyId={String(surveyId)}
                        engagementId={savedSurvey?.engagement_id || undefined}
                        formDefinition={formDefinition}
                        conditionalLinks={savedSurvey?.conditional_links}
                        readOnly={!canEdit}
                        focusQuestionKey={reportFocusKey}
                        engagementClosed={isEngagementClosed(savedEngagement)}
                        onNext={() => setTab(TAB_EXPORT)}
                        onCancel={() => navigate('/surveys')}
                    />
                </Box>
            )}
            {tab === TAB_EXPORT && (
                <Box role="tabpanel" id={tabIds(TAB_EXPORT).panel} aria-labelledby={tabIds(TAB_EXPORT).tab}>
                    <ExportSettingsPanel
                        ref={exportSettingsRef}
                        surveyId={String(surveyId)}
                        engagementId={savedSurvey?.engagement_id || undefined}
                        formDefinition={formDefinition}
                        conditionalLinks={savedSurvey?.conditional_links}
                        readOnly={!canEdit}
                        engagementClosed={isEngagementClosed(savedEngagement)}
                        onGoToReport={(questionKey) => handleTabChange(TAB_REPORT, questionKey)}
                        onSaved={() => navigate('/surveys')}
                        onCancel={() => navigate('/surveys')}
                    />
                </Box>
            )}
            <AutoSaveSnackBar
                open={autoSaveNotificationOpen}
                handleClose={() => {
                    setAutoSaveNotificationOpen(false);
                }}
            />
        </Box>
    );
};

export default SurveyFormBuilder;
